import { router, useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { interpolate, runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { AdaptivePlanCard } from '@/components/adaptive-plan-card';
import { BudgetPaceChart } from '@/components/budget-pace-chart';
import { CATEGORY_ICONS } from '@/components/category-grid';
import { DayEntriesList, DayTextSummary } from '@/components/forecast-views';
import { SummaryCalendar } from '@/components/summary-calendar';
import { EventRow } from '@/components/event-row';
import { MonthRing } from '@/components/month-ring';
import { TopToast, type TopToastContent } from '@/components/top-toast';
import { TutorialOverlay } from '@/components/tutorial-overlay';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { HeaderAvatar } from '@/components/ui/header-avatar';
import { IconTile } from '@/components/ui/icon-tile';
import { ProgressRing } from '@/components/ui/progress-ring';
import { IDEAS_HINT_THRESHOLD, useAppMetaStore } from '@/store/app-meta-store';
import { consumeHouseholdJoinResult, useAuthStore } from '@/store/auth-store';
import { cumulativeByDayGrOf, cumulativeUpToDayGrOf, entriesForMonth, spentGrOf, useBudgetStore } from '@/store/budget-store';
import { snapshotFor, useForecastHistoryStore } from '@/store/forecast-history-store';
import { totalSavingsGrOf, useGoalsStore } from '@/store/goals-store';
import { useFixedCostsStore } from '@/store/fixed-costs-store';
import { monthKeyOf, useIncomeStore } from '@/store/income-store';
import { usePoliciesStore } from '@/store/policies-store';
import { upcomingOf, useRemindersStore } from '@/store/reminders-store';
import { cloudSyncEnabled, setCloudSyncEnabled } from '@/sync/cloud-sync';
import { colors, radius, shadows, spacing } from '@/theme/tokens';
import { forecastStrengthOf, monthForecastOf, type MonthMarker, type PendingItem } from '@/utils/forecast';
import type { BudgetEntry } from '@/types/domain';
import { daysInMonthOf } from '@/utils/calendar';
import { currentMonthNamePl, daysInMonth, formatPln, formatWhenPl } from '@/utils/format';
import { periodSummaryOf, type SummaryPeriod } from '@/utils/period-summary';

const SUMMARY_PERIODS: SummaryPeriod[] = ['month', 'quarter', 'year'];
// How far the FINGER travels to open the shade. Deliberately independent of
// how tall the panel is — the panel measures itself (panelH) and hides by its
// own height. One constant doing both jobs broke as soon as the panel grew
// (calendar): either 160 px stuck over the top bar, or a 520 px drag to open.
const PULL_TRIGGER = 240;

function SummaryRow({ label, value, color, strong }: { label: string; value: string; color: string; strong?: boolean }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <AppText variant={strong ? 'labelLg' : 'bodyMd'} style={{ flex: 1 }}>
        {label}
      </AppText>
      <AppText variant={strong ? 'headlineSm' : 'labelLg'} color={color}>
        {value}
      </AppText>
    </View>
  );
}

/**
 * Start — budget-first dashboard per mock start_nowa_nawigacja: verdict
 * headline ("Twój portfel ma się dobrze"), budget hero with % chip + pace
 * chart, quick cards Cele/Polisy, recent expenses. Adding lives on the FAB.
 */
export default function HomeScreen() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const entries = useBudgetStore((s) => s.entries);
  const monthlyBudgetGr = useBudgetStore((s) => s.monthlyBudgetGr);
  const goals = useGoalsStore((s) => s.goals);
  const contributions = useGoalsStore((s) => s.contributions);
  const policies = usePoliciesStore((s) => s.policies);
  const polfinancePolicies = usePoliciesStore((s) => s.polfinancePolicies);
  const loadPolfinancePolicies = usePoliciesStore((s) => s.loadPolfinancePolicies);
  const reminders = useRemindersStore((s) => s.reminders);
  const screenViews = useAppMetaStore((s) => s.screenViews);
  const ideasHintDone = useAppMetaStore((s) => s.ideasHintDone);
  const dismissIdeasHint = useAppMetaStore((s) => s.dismissIdeasHint);
  const syncNudgeDone = useAppMetaStore((s) => s.syncNudgeDone);
  const dismissSyncNudge = useAppMetaStore((s) => s.dismissSyncNudge);
  const pendingSyncIntent = useAppMetaStore((s) => s.pendingSyncIntent);
  const setPendingSyncIntent = useAppMetaStore((s) => s.setPendingSyncIntent);
  // Cloud-sync module state is not reactive — re-read on every focus so the
  // nudge disappears right after the toggle flips anywhere in the app.
  const [syncOn, setSyncOn] = useState(cloudSyncEnabled());
  useFocusEffect(
    useCallback(() => {
      setSyncOn(cloudSyncEnabled());
    }, []),
  );

  // The user tapped "Zabezpiecz dane" while logged out and has just logged in:
  // honour the intent — enable the cloud copy automatically and clear the nudge.
  useEffect(() => {
    if (!account || !pendingSyncIntent) return;
    (async () => {
      await setCloudSyncEnabled(true);
      setSyncOn(true);
      await setPendingSyncIntent(false);
      await useAppMetaStore.getState().dismissSyncNudge();
    })();
  }, [account, pendingSyncIntent, setPendingSyncIntent]);
  // Load the Polfinance policies here too so the Start count is complete.
  useEffect(() => {
    if (account?.linkStatus === 'LINKED') void loadPolfinancePolicies();
  }, [account?.linkStatus, loadPolfinancePolicies]);
  const incomeSources = useIncomeStore((s) => s.sources);
  const incomeEntries = useIncomeStore((s) => s.entries);
  const fixedCosts = useFixedCostsStore((s) => s.costs);
  const showIdeasHint = !ideasHintDone && screenViews >= IDEAS_HINT_THRESHOLD;

  // Pull-down gesture (owner request): drag down at the top to reveal a summary
  // panel with a period switcher (Miesiąc / Kwartał / Rok).
  const [period, setPeriod] = useState<SummaryPeriod>('month');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  // The summary is a top "shade" that slides down like the phone's notification
  // panel — NO spinner, follows the finger, feels like it was always there
  // (owner: "niech się nasunie w stylu paska powiadomień, bez guzika ładowania").
  const shade = useSharedValue(0); // 0 = hidden above, 1 = fully pulled down
  // Panel height, measured by onLayout. Seeded with the WINDOW height so the
  // first frame hides the panel fully (over-estimating is harmless; under-
  // estimating would flash the calendar over the top bar).
  const panelH = useSharedValue(windowHeight);
  const closeShade = useCallback(() => {
    shade.value = withTiming(0, { duration: 200 });
    setSummaryOpen(false);
  }, [shade]);
  // Pull DOWN on the greeting area to reveal the summary shade. The visible
  // grabber pill was removed (owner: it just pushed everything down and wasted
  // space — the coach guide teaches the gesture). activeOffsetY keeps the
  // vertical ScrollView usable: only a deliberate downward drag opens the shade.
  const pullGesture = Gesture.Pan()
    .activeOffsetY(12)
    .failOffsetY(-12)
    .onUpdate((e) => {
      shade.value = Math.min(Math.max(e.translationY / PULL_TRIGGER, 0), 1);
    })
    .onEnd((e) => {
      if (e.translationY > PULL_TRIGGER * 0.4) {
        shade.value = withTiming(1);
        runOnJS(setSummaryOpen)(true);
      } else {
        shade.value = withTiming(0);
        runOnJS(setSummaryOpen)(false);
      }
    });
  // Whole-screen pull-down (user: only the tiny greeting worked): a Pan that
  // runs SIMULTANEOUSLY with the ScrollView's native gesture, so mid-list a
  // downward drag still scrolls the list normally; only once the list sits at
  // the top (scrollY <= 0) does the pull start driving the shade. pullBaseY
  // latches the finger position at that moment, so reaching the top mid-drag
  // continues into the shade without a jump.
  const scrollY = useSharedValue(0);
  const pullBaseY = useSharedValue(-1); // -1 = not pulling (list not at top yet)
  const scrollGesture = Gesture.Native();
  const screenPullGesture = Gesture.Pan()
    .simultaneousWithExternalGesture(scrollGesture)
    .activeOffsetY(12)
    .onBegin(() => {
      pullBaseY.value = -1;
    })
    .onUpdate((e) => {
      if (pullBaseY.value < 0) {
        if (scrollY.value > 0 || e.translationY <= 0) return; // let the list scroll
        pullBaseY.value = e.translationY;
      }
      shade.value = Math.min(Math.max((e.translationY - pullBaseY.value) / PULL_TRIGGER, 0), 1);
    })
    .onEnd(() => {
      if (pullBaseY.value < 0) return;
      if (shade.value > 0.4) {
        shade.value = withTiming(1);
        runOnJS(setSummaryOpen)(true);
      } else {
        shade.value = withTiming(0);
        runOnJS(setSummaryOpen)(false);
      }
    });
  const backdropStyle = useAnimatedStyle(() => ({ opacity: shade.value * 0.5 }));
  const panelStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: interpolate(shade.value, [0, 1], [-panelH.value, 0]) }],
  }));
  const summary = periodSummaryOf(period, new Date(), incomeEntries, entries, contributions);

  // Tapping a ring dot reveals what it is — as a toast that slides down from the
  // top, rests, and slides away (owner: no closable bubble at the bottom).
  const [markerToast, setMarkerToast] = useState<TopToastContent | null>(null);
  const toastKey = useRef(0);
  const onMarkerPress = (m: MonthMarker) => {
    toastKey.current += 1;
    setMarkerToast({
      key: toastKey.current,
      icon: m.kind === 'INCOME' ? 'arrow-down-circle-outline' : 'card-outline',
      iconColor: m.kind === 'INCOME' ? colors.primary : colors.warning,
      iconBg: m.kind === 'INCOME' ? colors.infoBg : colors.warningBg,
      title: `${m.kind === 'INCOME' ? t('forecast.markerIncome') : t('forecast.markerCost')}: ${m.label}`,
      subtitle: `${t('forecast.markerDay', { day: m.day })}${m.amountGr != null ? ` · ${formatPln(m.amountGr)}` : ''}`,
    });
  };

  // Ring nearly full-width (owner): screen margin (20×2) + card padding (16×2) = 72.
  const ringSize = Math.min(windowWidth - 72, 340);

  const today = new Date();
  const day = today.getDate();
  const dim = daysInMonth(today);

  const cumulative = cumulativeByDayGrOf(entries);
  const spentGr = spentGrOf(entries);
  const leftGr = monthlyBudgetGr - spentGr;
  const spentPct = monthlyBudgetGr > 0 ? Math.min(100, Math.round((100 * spentGr) / monthlyBudgetGr)) : 0;
  // budget 0 (fresh install) must not read as "over pace" — it reads as "not set up yet"
  const overPace = monthlyBudgetGr > 0 && spentGr > (monthlyBudgetGr * day) / dim;

  const savingsGr = totalSavingsGrOf(goals, contributions);
  // "active" = open-ended or protection end still ahead (same rule as the wallet).
  // Owner: Start counted only manually-added policies — include the Polfinance
  // ones (from the CRM) so the number matches reality.
  const todayIso = today.toISOString().slice(0, 10);
  const activeLocalPolicies = policies.filter((p) => p.openEnded || !p.endDate || p.endDate >= todayIso).length;
  const activePolfinancePolicies = polfinancePolicies.filter(
    (p) => p.active !== false && (!p.endDate || p.endDate >= todayIso),
  ).length;
  const activePolicies = activeLocalPolicies + activePolfinancePolicies;
  const urgent = upcomingOf(reminders, 14).slice(0, 2);
  const recent = entries.slice(0, 3);
  // Greeting name: the linked client's first name, else the name the user typed
  // in onboarding, else nobody. Always used as-is (mianownik) — we never decline
  // it, so we can't produce a wrong vocative (owner: "może nie odmieniać").
  const userName = useAppMetaStore((s) => s.userName);
  const greetName = account?.firstName ?? userName ?? null;

  // Forecast (Napper-style): month ring + predictions + strength score.
  const forecast = monthForecastOf(incomeSources, incomeEntries, fixedCosts, entries, monthlyBudgetGr, today);
  const strength = forecastStrengthOf(incomeSources, incomeEntries, fixedCosts, entries, goals.length, today);
  const showRing = forecast.markers.length > 0 || forecast.leftGr !== null;

  // Adaptive plan (Napper): overdue items ask on Start; "Jeszcze nie" snoozes
  // the row for this visit only — next app open asks again, an answer ends it.
  const monthKey = monthKeyOf(today);
  const [snoozedPending, setSnoozedPending] = useState<string[]>([]);
  const visiblePending = forecast.pending.filter((p) => !snoozedPending.includes(`${p.kind}-${p.id}`));

  // The visible "plan updated" confirmation — recomputed from FRESH store state
  // so the toast shows the number the answer just produced, not the stale one.
  const planUpdatedToast = () => {
    const f = monthForecastOf(
      useIncomeStore.getState().sources,
      useIncomeStore.getState().entries,
      useFixedCostsStore.getState().costs,
      useBudgetStore.getState().entries,
      useBudgetStore.getState().monthlyBudgetGr,
      new Date(),
    );
    toastKey.current += 1;
    setMarkerToast({
      key: toastKey.current,
      icon: 'checkmark-circle-outline',
      iconColor: colors.primary,
      iconBg: colors.infoBg,
      title: t('adaptive.updatedTitle'),
      subtitle:
        f.leftGr === null
          ? t('adaptive.updatedNoData')
          : f.leftGr >= 0
            ? t('forecast.leftPositive', { amount: formatPln(f.leftGr) })
            : t('forecast.leftNegative', { amount: formatPln(-f.leftGr) }),
    });
  };
  const onPendingIncomeConfirm = (id: string, amountGr: number) => {
    void useIncomeStore.getState().setEntry(id, monthKey, amountGr).then(planUpdatedToast);
  };
  const onPendingSkip = (item: PendingItem) => {
    const done =
      item.kind === 'INCOME'
        ? useIncomeStore.getState().skipMonth(item.id, monthKey)
        : useFixedCostsStore.getState().skipMonth(item.id, monthKey);
    void done.then(planUpdatedToast);
  };
  const onPendingCostPaid = (id: string) => {
    void useFixedCostsStore.getState().markPaid(id, monthKey).then(planUpdatedToast);
  };
  const onPendingLater = (item: PendingItem) => setSnoozedPending((s) => [...s, `${item.kind}-${item.id}`]);

  // --- Summary shade: calendar + per-day chart (mock poprawione_podsumowanie_linia) ---
  // Local date ISO (todayIso above is UTC-based; near midnight it can point at
  // the wrong calendar day for the user).
  const todayLocalIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  // Selected day is session-local on purpose — persisting it would trap a
  // returning user in a past day.
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  // Past months are not in the store (hydrate loads the current month only);
  // the chevron loads them lazily and caches per month key.
  const [pastMonthEntries, setPastMonthEntries] = useState<Record<string, BudgetEntry[]>>({});
  const viewMonthKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
  const isCurrentViewMonth = viewMonthKey === monthKey;
  const viewEntries = isCurrentViewMonth ? entries : (pastMonthEntries[viewMonthKey] ?? []);
  useEffect(() => {
    if (isCurrentViewMonth || pastMonthEntries[viewMonthKey]) return;
    void entriesForMonth(viewMonthKey).then((rows) =>
      setPastMonthEntries((s) => ({ ...s, [viewMonthKey]: rows })),
    );
  }, [isCurrentViewMonth, viewMonthKey, pastMonthEntries]);
  const onShiftCalendarMonth = (delta: -1 | 1) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    // Never navigate past the current month — the future was never forecast.
    if (`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` > monthKey) return;
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setSelectedIso(null); // selection only ever points into the viewed month
  };
  const viewDim = daysInMonthOf(viewYear, viewMonth);
  // Default focus: today in the current month, the month's last day in a past one.
  const effectiveIso = selectedIso ?? (isCurrentViewMonth ? todayLocalIso : `${viewMonthKey}-${String(viewDim).padStart(2, '0')}`);
  const effectiveDay = Number(effectiveIso.slice(8, 10));
  const dayCumulative = cumulativeUpToDayGrOf(viewEntries, effectiveDay);
  const calendarHasData = (iso: string) => viewEntries.some((e) => e.entryDate === iso);
  // Upcoming-cost bubbles on the plan line — CURRENT month only; today's plan
  // labels on a past month's chart would be a fake past.
  const planMarkers = isCurrentViewMonth
    ? fixedCosts
        .filter((c) => c.active && c.dueDay !== null && c.dueDay >= day && !c.paidMonths.includes(monthKey))
        .map((c) => ({ day: c.dueDay as number, label: c.name }))
    : [];
  const historyByDay = useForecastHistoryStore((s) => s.byDay);
  const daySnapshot = snapshotFor(historyByDay, effectiveIso);
  const startView = useAppMetaStore((s) => s.startView);
  const setStartView = useAppMetaStore((s) => s.setStartView);
  const monthGen = t(`forecastHistory.monthGen.${Number(effectiveIso.slice(5, 7))}`);
  const dayEntries = viewEntries.filter((e) => e.entryDate === effectiveIso);

  // Onboarding invite auto-join finished (auth-store) — tell the user once.
  useEffect(() => {
    const joined = consumeHouseholdJoinResult();
    if (!joined) return;
    toastKey.current += 1;
    setMarkerToast({
      key: toastKey.current,
      icon: joined.ok ? 'people-outline' : 'alert-circle-outline',
      iconColor: joined.ok ? colors.primary : colors.warning,
      iconBg: joined.ok ? colors.infoBg : colors.warningBg,
      title: joined.ok
        ? t('household.joinedToast', { name: joined.name ?? '' })
        : t('household.joinFailedToast'),
      subtitle: joined.ok ? t('household.joinedToastSub') : t('household.joinFailedToastSub'),
    });
  }, [account, t]);

  // "wow": the first time the month forecast has REAL numbers (income data is
  // in) → ask for a rating once, ever. Small delay so Start paints before the
  // ask. Works for single-income users too (unlike the old Zarobki gate).
  const forecastReady = forecast.leftGr !== null;
  useEffect(() => {
    if (!forecastReady || useAppMetaStore.getState().ratingDone) return;
    const timer = setTimeout(() => {
      if (!useAppMetaStore.getState().ratingDone) router.push('/rate');
    }, 1500);
    return () => clearTimeout(timer);
  }, [forecastReady]);

  // Ring center mode (owner: "klik w środek zmienia na stałe opcję") — either
  // "zostanie do końca miesiąca" (default) or Napper-style "następna płatność
  // za X dni ~Y zł". The nearest upcoming event drives mode B.
  const ringNextMode = useAppMetaStore((s) => s.ringNextMode);
  const setRingNextMode = useAppMetaStore((s) => s.setRingNextMode);
  const nextEvent = forecast.markers.find((m) => m.day >= day) ?? null;
  const showNextInRing = ringNextMode && nextEvent !== null;
  const daysToNext = nextEvent ? nextEvent.day - day : 0;
  const nextWhenText =
    daysToNext <= 0 ? t('common.today') : daysToNext === 1 ? t('common.tomorrow') : t('common.inDays', { count: daysToNext });

  // Sync nudge: real data + no cloud copy = one dismissible reminder (owner:
  // "jak ktoś doda polisę, ocenę i coś wpisze — zaproponuj sync, żeby nie stracił").
  const hasMeaningfulData = policies.length > 0 || incomeEntries.length >= 3 || entries.length >= 5;
  const showSyncNudge = hasMeaningfulData && !syncNudgeDone && !(account && syncOn);

  // Smart CTA (owner bug report: it used to dump the user in settings):
  // logged out -> login flow, remembering the intent; logged in -> enable NOW.
  const onSyncNudgeCta = async () => {
    if (!account) {
      await setPendingSyncIntent(true);
      router.push('/auth/login');
      return;
    }
    await setCloudSyncEnabled(true);
    setSyncOn(true);
    await dismissSyncNudge();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <GestureDetector gesture={screenPullGesture}>
      <GestureDetector gesture={scrollGesture}>
      <ScrollView
        onScroll={(e) => {
          scrollY.value = e.nativeEvent.contentOffset.y;
        }}
        scrollEventThrottle={16}
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.lg, paddingBottom: spacing.xl }}
      >
        {/* Top bar: avatar + Siła prognozy badge (Napper-style) + gift + bell.
            The grabber pill above was removed (owner: wasted vertical space; the
            coach guide still teaches the pull-down-for-summary gesture). */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
          <HeaderAvatar />
          {/* Score badge — tap explains the forecast stages. A real mini progress
              ring (not just a decorative outline) so the fill actually reflects
              the score, like the big ring on the explainer screen. */}
          <Pressable accessibilityRole="button" accessibilityLabel={t('strength.title')} onPress={() => router.push('/forecast-strength')}>
            <ProgressRing percent={strength.score} size={40} strokeWidth={4}>
              <AppText variant="labelMd" color={colors.navy} style={{ fontSize: 12 }}>
                {strength.score}
              </AppText>
            </ProgressRing>
          </Pressable>
          {/* App-name title removed (owner: didn't fit next to the score) — a
              spacer keeps the gift/bell pinned to the right. */}
          <View style={{ flex: 1 }} />
          {/* Ideas & bugs board — was hard to find (owner: tester didn't know he
              could report there). Always-visible entry point, not just the hint. */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('ideas.title')}
            onPress={() => {
              if (!ideasHintDone) void dismissIdeasHint();
              router.push('/ideas');
            }}
            style={{ minWidth: 40, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="bulb-outline" size={23} color={colors.navy} />
          </Pressable>
          {/* Gift -> refer (Napper pattern) */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('more.refer')}
            onPress={() => router.push('/refer')}
            style={{ minWidth: 40, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="gift-outline" size={23} color={colors.navy} />
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('home.urgent')}
            onPress={() => router.push('/reminders')}
            style={{ minWidth: 40, minHeight: 44, alignItems: 'flex-end', justifyContent: 'center' }}
          >
            <Ionicons name="notifications-outline" size={24} color={colors.navy} />
          </Pressable>
        </View>

        {/* Greeting + budget verdict as the headline (mock: "Twój portfel ma się
            dobrze."). Doubles as the pull-down surface for the summary shade. */}
        <GestureDetector gesture={pullGesture}>
          <View style={{ gap: spacing.base }}>
            <AppText variant="bodyLg" color={colors.textSecondary}>
              {greetName ? t('home.greeting', { name: greetName }) : t('home.greetingNoName')}
            </AppText>
            <AppText variant="headlineLg" color={colors.textPrimary}>
              {monthlyBudgetGr === 0
                ? t('home.verdictNoBudget')
                : overPace
                  ? t('home.verdictOverPace')
                  : t('home.verdictOnTrack')}
            </AppText>
          </View>
        </GestureDetector>

        {/* "Coś się zmieniło" — overdue plan items ask for an answer (mock:
            karta_co_si_zmieni_o). The plan never assumes silently. */}
        <AdaptivePlanCard
          pending={visiblePending}
          onIncomeConfirm={onPendingIncomeConfirm}
          onSkip={onPendingSkip}
          onCostPaid={onPendingCostPaid}
          onLater={onPendingLater}
        />

        {/* "Twój miesiąc" — full month-forecast ring (mock: pulpit_prognoza_finansowa):
            a closed progress circle with the amount in the centre and Przychody/
            Wydatki below. Shows once there is anything to predict. */}
        {showRing && (
          <Card style={{ gap: spacing.sm, alignItems: 'center' }}>
            <AppText variant="headlineMd">{t('forecast.title')}</AppText>
            {/* Owner: the Siła prognozy badge/forecast was a mystery number — say
                out loud what it's for, right on the card (2026-07-18). */}
            <AppText variant="labelMd" color={colors.outline} style={{ textAlign: 'center' }}>
              {t('forecast.learnCaption')}
            </AppText>
            {/* Owner: the Stitch ring crammed the amount into the centre where the
                side bubbles sit, so labels overlapped the number. Fixed by keeping
                ONLY a small "Dziś · dzień" chip inside the ring (never collides
                with the timeline bubbles) and moving the big amount BELOW the arc,
                into the open gap at the bottom. */}
            <MonthRing markers={forecast.markers} today={day} daysInMonth={dim} size={ringSize} stroke={22} onMarkerPress={onMarkerPress}>
              <View
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 72,
                  height: 72,
                  borderRadius: 36,
                  borderWidth: 2.5,
                  borderColor: colors.navy,
                  backgroundColor: colors.card,
                }}
              >
                <AppText variant="labelMd" color={colors.textSecondary} style={{ letterSpacing: 1 }}>
                  {t('common.today')}
                </AppText>
                <AppText variant="headlineMd" color={colors.navy}>
                  {day}
                </AppText>
              </View>
            </MonthRing>

            {/* The prediction itself — tap to switch (and remember) the mode. */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('forecast.tapAmountHint')}
              onPress={() => void setRingNextMode(!ringNextMode)}
              style={{ alignItems: 'center', gap: 2 }}
            >
              {showNextInRing && nextEvent ? (
                <>
                  <AppText
                    variant="labelMd"
                    color={colors.textSecondary}
                    style={{ letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'center' }}
                  >
                    {nextEvent.kind === 'INCOME' ? t('forecast.modeNextIncome') : t('forecast.modeNextCost')}{' '}
                    {nextWhenText}
                  </AppText>
                  <AppText variant="headlineLg" color={colors.primary} style={{ textAlign: 'center' }}>
                    {nextEvent.amountGr != null ? `~${formatPln(nextEvent.amountGr)}` : nextEvent.label}
                  </AppText>
                  <AppText variant="labelMd" color={colors.textSecondary} numberOfLines={1} style={{ textAlign: 'center' }}>
                    {nextEvent.label}
                  </AppText>
                </>
              ) : forecast.leftGr !== null ? (
                <>
                  <AppText variant="labelMd" color={colors.textSecondary} style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                    {forecast.leftGr >= 0 ? t('forecast.centerLabelPositive') : t('forecast.centerLabelNegative')}
                  </AppText>
                  <AppText variant="headlineLg" color={colors.primary} style={{ textAlign: 'center', fontSize: 32, lineHeight: 40 }}>
                    {formatPln(Math.abs(forecast.leftGr))}
                  </AppText>
                  <AppText variant="bodyMd" color={colors.textSecondary}>
                    {t('forecast.toMonthEnd')}
                  </AppText>
                </>
              ) : (
                <AppText variant="labelMd" color={colors.textSecondary} style={{ textAlign: 'center' }}>
                  {t('forecast.subtitle')}
                </AppText>
              )}
            </Pressable>

            {/* Przychody | Wydatki mini-cards */}
            <View style={{ flexDirection: 'row', gap: spacing.sm, alignSelf: 'stretch' }}>
              <View
                style={{
                  flex: 1,
                  backgroundColor: colors.background,
                  borderRadius: radius.card,
                  padding: spacing.md,
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <AppText variant="labelMd" color={colors.textSecondary} style={{ textAlign: 'center' }}>
                  {t('forecast.incomePlan')}
                </AppText>
                <AppText variant="headlineSm" color={colors.textPrimary}>
                  {formatPln(forecast.incomeGr)}
                </AppText>
              </View>
              <View
                style={{
                  flex: 1,
                  backgroundColor: colors.background,
                  borderRadius: radius.card,
                  padding: spacing.md,
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <AppText variant="labelMd" color={colors.textSecondary} style={{ textAlign: 'center' }}>
                  {t('forecast.outflowPlan')}
                </AppText>
                <AppText variant="headlineSm" color={colors.textPrimary}>
                  {formatPln(forecast.outflowGr)}
                </AppText>
              </View>
            </View>

            {/* Owner bug: user saw 25 zł spent in Budżet but ~59 zł here and was
                confused. These are a PROJECTION for the whole month (pace + fixed
                costs), not what's been spent — say so explicitly. */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, alignSelf: 'stretch' }}>
              <Ionicons name="information-circle-outline" size={15} color={colors.outline} style={{ marginTop: 1 }} />
              <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
                {t('forecast.planNote', { spent: formatPln(spentGr) })}
              </AppText>
            </View>

            {forecast.runsOutDay !== null ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.sm,
                  backgroundColor: colors.warningBg,
                  borderRadius: radius.card,
                  padding: spacing.md,
                  alignSelf: 'stretch',
                }}
              >
                <Ionicons name="alert-circle-outline" size={20} color={colors.warning} />
                <AppText variant="bodyMd" style={{ flex: 1 }}>
                  {t('forecast.runsOut', { day: forecast.runsOutDay })}
                </AppText>
              </View>
            ) : (
              forecast.leftGr !== null &&
              forecast.leftGr > 0 && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing.sm,
                    backgroundColor: colors.infoBg,
                    borderRadius: radius.card,
                    padding: spacing.md,
                    alignSelf: 'stretch',
                  }}
                >
                  <Ionicons name="trending-up-outline" size={20} color={colors.primary} />
                  <AppText variant="bodyMd" style={{ flex: 1 }}>
                    {t('forecast.willSave', { amount: formatPln(forecast.leftGr) })}
                  </AppText>
                </View>
              )
            )}
          </Card>
        )}

        {/* Sync nudge — user has real data but no cloud copy (dismissible). */}
        {showSyncNudge && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.warningBg }}>
            <IconTile icon="cloud-upload-outline" bg={colors.warning} fg="#FFFFFF" size={44} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('home.syncNudgeTitle')}
              </AppText>
              <AppText variant="bodyMd">{t('home.syncNudgeBody')}</AppText>
              <Pressable
                accessibilityRole="button"
                onPress={() => void onSyncNudgeCta()}
                style={{ minHeight: 36, justifyContent: 'center' }}
              >
                <AppText variant="labelLg" color={colors.primary}>
                  {account ? t('home.syncNudgeCtaEnable') : t('home.syncNudgeCta')} →
                </AppText>
              </Pressable>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Zamknij"
              onPress={() => dismissSyncNudge()}
              style={{ minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={20} color={colors.outline} />
            </Pressable>
          </Card>
        )}

        {/* One-off nudge to discover the community board once the user has
            explored a bit (owner request). Dismiss = never shown again. */}
        {showIdeasHint && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <IconTile icon="bulb-outline" bg={colors.primary} fg="#FFFFFF" size={44} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('home.ideasHintTitle')}
              </AppText>
              <AppText variant="bodyMd">{t('home.ideasHintBody')}</AppText>
              <Pressable
                accessibilityRole="button"
                onPress={() => {
                  void dismissIdeasHint();
                  router.push('/ideas' as never);
                }}
                style={{ minHeight: 36, justifyContent: 'center' }}
              >
                <AppText variant="labelLg" color={colors.primary}>
                  {t('home.ideasHintCta')} →
                </AppText>
              </Pressable>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Zamknij"
              onPress={() => dismissIdeasHint()}
              style={{ minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={20} color={colors.outline} />
            </Pressable>
          </Card>
        )}

        {/* Budget hero: remaining + % spent chip + pace chart */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <AppText variant="labelMd" style={{ flex: 1, letterSpacing: 1.2, textTransform: 'uppercase' }}>
              {t('home.budgetTitle', { month: currentMonthNamePl() })}
            </AppText>
            <View
              style={{
                backgroundColor: overPace ? colors.warningBg : colors.primaryTile,
                borderRadius: radius.full,
                paddingHorizontal: spacing.sm,
                paddingVertical: 2,
              }}
            >
              <AppText variant="labelMd" color={overPace ? colors.warning : colors.navy}>
                {t('home.spentPct', { pct: spentPct })}
              </AppText>
            </View>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: spacing.xs }}>
            <AppText variant="headlineLg" color={colors.primary} style={{ fontSize: 40, lineHeight: 48 }}>
              {formatPln(leftGr)}
            </AppText>
            <AppText variant="bodyMd">{t('home.leftShort')}</AppText>
          </View>
          {monthlyBudgetGr > 0 ? (
            <BudgetPaceChart budgetGr={monthlyBudgetGr} cumulativeSpentGr={cumulative} daysInMonth={dim} />
          ) : (
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push('/budget')}
              style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, minHeight: 44 }}
            >
              <Ionicons name="create-outline" size={20} color={colors.primary} />
              <AppText variant="labelLg" color={colors.primary}>
                {t('home.setBudgetCta')}
              </AppText>
            </Pressable>
          )}
        </Card>

        {/* Quick cards: goals savings + active policies */}
        <View style={{ flexDirection: 'row', gap: spacing.sm }}>
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push('/goals')}
            style={({ pressed }) => ({
              flex: 1,
              backgroundColor: colors.card,
              borderRadius: radius.card,
              padding: spacing.md,
              gap: spacing.xs,
              transform: [{ scale: pressed ? 0.98 : 1 }],
              ...shadows.card,
            })}
          >
            <Ionicons name="flag-outline" size={24} color={colors.warning} />
            <AppText variant="bodyMd">{t('tabs.goals')}</AppText>
            <AppText variant="headlineSm" color={colors.textPrimary}>
              {formatPln(savingsGr)}
            </AppText>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push('/policies')}
            style={({ pressed }) => ({
              flex: 1,
              backgroundColor: colors.card,
              borderRadius: radius.card,
              padding: spacing.md,
              gap: spacing.xs,
              transform: [{ scale: pressed ? 0.98 : 1 }],
              ...shadows.card,
            })}
          >
            <Ionicons name="shield-checkmark-outline" size={24} color={colors.navy} />
            <AppText variant="bodyMd">{t('tabs.policies')}</AppText>
            <AppText variant="headlineSm" color={colors.textPrimary}>
              {t('home.activePolicies', { count: activePolicies })}
            </AppText>
          </Pressable>
        </View>

        {/* Recent expenses */}
        {recent.length > 0 && (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <AppText variant="headlineSm">{t('home.recentEntries')}</AppText>
              <AppText variant="labelLg" color={colors.primary} onPress={() => router.push('/budget')}>
                {t('common.seeAll')}
              </AppText>
            </View>
            <Card style={{ padding: 0 }}>
              {recent.map((entry, idx) => (
                // Tap an expense right here to edit it (owner: had to open
                // "Zobacz wszystkie" first). Deep-links to Budżet with the entry
                // already open for editing.
                <Pressable
                  key={entry.id}
                  accessibilityRole="button"
                  onPress={() => router.push({ pathname: '/(tabs)/budget', params: { editEntry: entry.id } })}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: spacing.md,
                    borderTopWidth: idx === 0 ? 0 : 1,
                    borderTopColor: colors.divider,
                  }}
                >
                  <IconTile icon={CATEGORY_ICONS[entry.categoryId]} bg={colors.primaryTile} fg={colors.navy} size={44} />
                  <View style={{ flex: 1 }}>
                    <AppText variant="labelLg">{entry.note ?? t(`budget.categories.${entry.categoryId}`)}</AppText>
                    <AppText variant="bodyMd">
                      {entry.note ? `${t(`budget.categories.${entry.categoryId}`)} · ${formatWhenPl(entry.createdAt)}` : formatWhenPl(entry.createdAt)}
                    </AppText>
                  </View>
                  <AppText variant="labelLg" color={colors.error}>
                    −{formatPln(entry.amountGr)}
                  </AppText>
                  <Ionicons name="chevron-forward" size={16} color={colors.outlineVariant} />
                </Pressable>
              ))}
            </Card>
          </>
        )}

        {/* Urgent deadlines (only when something is close) */}
        {urgent.length > 0 && (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <AppText variant="headlineSm">{t('home.urgent')}</AppText>
              <AppText variant="labelLg" color={colors.primary} onPress={() => router.push('/reminders')}>
                {t('common.seeAll')}
              </AppText>
            </View>
            <View style={{ gap: spacing.sm }}>
              {urgent.map((reminder) => (
                // An ending policy jumps straight to that policy (owner: it used
                // to dump you in the reminders list). Non-policy rows still open
                // the reminders screen.
                <EventRow
                  key={reminder.id}
                  reminder={reminder}
                  onPress={() =>
                    reminder.policyId
                      ? router.push({ pathname: '/policy/[id]', params: { id: reminder.policyId } })
                      : router.push('/reminders')
                  }
                />
              ))}
            </View>
          </>
        )}
      </ScrollView>
      </GestureDetector>
      </GestureDetector>

      {/* Dim scrim under the shade (tap to close). */}
      <Animated.View
        pointerEvents={summaryOpen ? 'auto' : 'none'}
        style={[
          { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: colors.navy, zIndex: 1 },
          backdropStyle,
        ]}
      >
        <Pressable style={{ flex: 1 }} accessibilityLabel={t('common.close')} onPress={closeShade} />
      </Animated.View>

      {/* The summary "shade" itself — slides down from the top. It measures
          its own height (panelH) so the hide offset is always right, and the
          body scrolls INSIDE the card on small screens. Safe: the panel is a
          SIBLING of the gesture-wrapped ScrollView, nothing composes. */}
      <Animated.View
        pointerEvents="box-none"
        onLayout={(e) => {
          panelH.value = e.nativeEvent.layout.height;
        }}
        style={[{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, padding: spacing.screen }, panelStyle]}
      >
        <Card style={{ gap: spacing.sm, maxHeight: windowHeight * 0.85 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <AppText variant="headlineSm" style={{ flex: 1 }}>
              {t('startSummary.title')}
            </AppText>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              onPress={closeShade}
              hitSlop={8}
            >
              <Ionicons name="close" size={22} color={colors.outline} />
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ gap: spacing.sm }} showsVerticalScrollIndicator={false}>
            {/* Period switcher pill */}
            <View style={{ flexDirection: 'row', backgroundColor: colors.infoBg, borderRadius: radius.full, padding: 3 }}>
              {SUMMARY_PERIODS.map((p) => {
                const active = p === period;
                return (
                  <Pressable
                    key={p}
                    accessibilityRole="button"
                    onPress={() => setPeriod(p)}
                    style={{
                      flex: 1,
                      paddingVertical: spacing.xs,
                      borderRadius: radius.full,
                      backgroundColor: active ? colors.primary : 'transparent',
                      alignItems: 'center',
                    }}
                  >
                    <AppText variant="labelMd" color={active ? '#FFFFFF' : colors.navy}>
                      {t(`startSummary.period.${p}`)}
                    </AppText>
                  </Pressable>
                );
              })}
            </View>

            {/* Calendar — tap a day, see the chart as of that day (owner:
                "wyświetlać stan tego wykresu na każdy dzień"). */}
            <SummaryCalendar
              viewYear={viewYear}
              viewMonth={viewMonth}
              selectedIso={selectedIso}
              todayIso={todayLocalIso}
              hasData={calendarHasData}
              onSelect={setSelectedIso}
              onShiftMonth={onShiftCalendarMonth}
            />

            {/* Selected-day body — chart / list / text, per the 3-icon pill. */}
            {startView === 'ring' && (
              <View style={{ gap: spacing.xs }}>
                <AppText variant="labelLg" color={colors.navy}>
                  {t('forecastHistory.chartTitle')}
                </AppText>
                <BudgetPaceChart
                  budgetGr={monthlyBudgetGr}
                  cumulativeSpentGr={dayCumulative}
                  daysInMonth={viewDim}
                  monthLabel={t(`forecastHistory.month.${viewMonth + 1}`)}
                  showTodayLabel={effectiveIso === todayLocalIso}
                  planMarkers={planMarkers}
                />
                <AppText variant="labelMd" color={colors.textSecondary} style={{ textAlign: 'center' }}>
                  {t('forecastHistory.stateAt', { day: effectiveDay, month: monthGen })}
                </AppText>
              </View>
            )}
            {startView === 'list' && <DayEntriesList dayEntries={dayEntries} />}
            {startView === 'text' && (
              <DayTextSummary
                dateLabel={`${effectiveDay} ${monthGen}`}
                daySpentGr={dayEntries.reduce((s, e) => s + e.amountGr, 0)}
                monthSpentGr={dayCumulative[dayCumulative.length - 1] ?? 0}
                forecastLeftGr={daySnapshot?.leftGr ?? null}
              />
            )}

            {/* Honest per-day forecast — only when that day's snapshot exists
                (the forecast, unlike expenses, is not recomputable backwards). */}
            {startView === 'ring' && daySnapshot && daySnapshot.leftGr !== null && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.sm,
                  backgroundColor: colors.infoBg,
                  borderRadius: radius.card,
                  padding: spacing.sm,
                }}
              >
                <Ionicons name="calculator-outline" size={18} color={colors.primary} />
                <AppText variant="bodyMd" style={{ flex: 1 }}>
                  {t('forecastHistory.forecastOfDay')}
                </AppText>
                <AppText variant="labelLg" color={colors.primary}>
                  {daySnapshot.leftGr >= 0
                    ? t('forecast.leftPositive', { amount: formatPln(daySnapshot.leftGr) })
                    : t('forecast.leftNegative', { amount: formatPln(-daySnapshot.leftGr) })}
                </AppText>
              </View>
            )}

            {/* Rows */}
            <View style={{ gap: spacing.xs, marginTop: spacing.xs }}>
              <SummaryRow label={t('startSummary.income')} value={formatPln(summary.incomeGr)} color={colors.navy} />
              <SummaryRow label={t('startSummary.expense')} value={formatPln(summary.expenseGr)} color={colors.navy} />
              <SummaryRow
                label={t('startSummary.balance')}
                value={formatPln(summary.balanceGr)}
                color={summary.balanceGr >= 0 ? colors.primary : colors.error}
                strong
              />
              <SummaryRow label={t('startSummary.saved')} value={formatPln(summary.savedGr)} color={colors.navy} />
            </View>

            {/* The "3 ikonki" view pill — chart / list / text, persisted. */}
            <View
              style={{
                flexDirection: 'row',
                alignSelf: 'center',
                backgroundColor: colors.infoBg,
                borderRadius: radius.full,
                padding: 3,
                gap: 3,
              }}
            >
              {(
                [
                  { key: 'ring', icon: 'pie-chart-outline', labelKey: 'forecastHistory.view.ring' },
                  { key: 'list', icon: 'list-outline', labelKey: 'forecastHistory.view.list' },
                  { key: 'text', icon: 'document-text-outline', labelKey: 'forecastHistory.view.text' },
                ] as const
              ).map((v) => {
                const active = startView === v.key;
                return (
                  <Pressable
                    key={v.key}
                    accessibilityRole="button"
                    accessibilityLabel={t(v.labelKey)}
                    onPress={() => void setStartView(v.key)}
                    style={{
                      width: 44,
                      height: 36,
                      borderRadius: radius.full,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: active ? colors.primary : 'transparent',
                    }}
                  >
                    <Ionicons name={v.icon} size={18} color={active ? '#FFFFFF' : colors.navy} />
                  </Pressable>
                );
              })}
            </View>
          </ScrollView>
        </Card>
      </Animated.View>

      {/* Ring-marker detail toast — slides in from the top, then away. */}
      <TopToast content={markerToast} onHide={() => setMarkerToast(null)} />

      {/* First-run coach guide (once, after onboarding) — replayable from Więcej. */}
      <TutorialOverlay />
    </SafeAreaView>
  );
}
